import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ExcelJS from 'exceljs';
import reportService from '../../services/reportService';
import { getWarehouses } from '../../services/warehousesService';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import Toast from '../../components/Common/Toast';
import {
  formatMoney,
  formatQty,
  formatPct,
  formatShortDate,
} from './InventoryDashboard/shared';

const todayIso = () => new Date().toISOString().slice(0, 10);

const monthStartIso = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};

const EntryChangeAlerts = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [dateFrom, setDateFrom] = useState(monthStartIso);
  const [dateTo, setDateTo] = useState(todayIso);
  const [warehouseId, setWarehouseId] = useState('');
  const [dailySummary, setDailySummary] = useState([]);
  const [detailRows, setDetailRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [detailMeta, setDetailMeta] = useState(null);
  const [selectedDetailDate, setSelectedDetailDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedWarehouses = useRef(false);

  useEffect(() => {
    if (!hasLoadedWarehouses.current) {
      hasLoadedWarehouses.current = true;
      getWarehouses({ active_only: true })
        .then((r) => {
          if (r.success && r.data) {
            setWarehouses(filterWarehousesForUser(r.data, authService.getCurrentUser()));
          }
        })
        .catch(() => {});
      loadRangeReport(monthStartIso(), todayIso(), '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRangeReport = async (from = dateFrom, to = dateTo, wh = warehouseId) => {
    setLoading(true);
    setSelectedDetailDate(null);
    setDetailRows([]);
    setDetailMeta(null);
    try {
      const params = { date_from: from, date_to: to };
      if (wh) params.warehouse_id = wh;
      const res = await reportService.getEntryChangeAlertsReport(params);
      if (res.success) {
        setDailySummary(res.daily_summary || []);
        setMeta(res.meta || null);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el reporte',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadDetailForDate = async (referenceDate) => {
    setDetailLoading(true);
    setSelectedDetailDate(referenceDate);
    try {
      const params = { reference_date: referenceDate, limit: 500 };
      if (warehouseId) params.warehouse_id = warehouseId;
      const res = await reportService.getEntryChangeAlertsReport(params);
      if (res.success) {
        setDetailRows(res.data || []);
        setDetailMeta(res.meta || null);
      } else {
        setToast({ isOpen: true, message: res.message || 'Error al cargar detalle', type: 'error' });
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el detalle',
        type: 'error',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleConsult = () => loadRangeReport(dateFrom, dateTo, warehouseId);

  const exportSummaryExcel = async () => {
    if (!dailySummary.length) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Resumen variaciones');
    ws.addRow(['Fecha', 'Comparado con', 'Cantidad de variaciones']);
    dailySummary.forEach((row) => {
      ws.addRow([row.reference_date, row.previous_date, row.variation_count]);
    });
    ws.addRow([]);
    ws.addRow(['Total período', '', meta?.total_variations ?? 0]);
    ws.getRow(1).font = { bold: true };
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Entradas_cambio_costo_resumen_${dateFrom}_${dateTo}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportDetailExcel = async () => {
    if (!detailRows.length || !selectedDetailDate) return;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Detalle día');
    const showWarehouse = detailMeta?.detail_by_warehouse;
    const headers = showWarehouse
      ? ['Almacén', 'SKU', 'Producto', 'Unidad', 'Día anterior', 'Costo unit. anterior S/', 'Día actual', 'Costo unit. actual S/', 'Variación S/', 'Variación %', 'Cant. actual', 'Valor actual S/']
      : ['SKU', 'Producto', 'Unidad', 'Día anterior', 'Costo unit. anterior S/', 'Día actual', 'Costo unit. actual S/', 'Variación S/', 'Variación %', 'Cant. actual', 'Valor actual S/'];
    ws.addRow(headers);
    detailRows.forEach((row) => {
      const base = [
        row.sku || '',
        row.name,
        row.unit_of_measure || '',
        row.previous_date,
        row.previous_unit_cost,
        row.current_date,
        row.current_unit_cost,
        row.change_unit_cost,
        row.change_ratio_pct != null ? row.change_ratio_pct : '',
        row.current_qty,
        row.current_value,
      ];
      ws.addRow(showWarehouse ? [row.warehouse_code || row.warehouse_name || '', ...base] : base);
    });
    ws.getRow(1).font = { bold: true };
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Entradas_cambio_costo_${selectedDetailDate}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const showWarehouse = detailMeta?.detail_by_warehouse;
  const daysWithData = dailySummary.filter((row) => row.variation_count > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-[1400px] mx-auto">
          <nav className="text-sm text-gray-600 mb-3">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/warehouses" className="hover:text-teal-600">Almacenes</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Entradas con cambio de costo</span>
          </nav>

          <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Entradas con cambio de costo</h1>
              <p className="text-gray-600 mt-1 max-w-3xl">
                Resumen de variaciones de costo unitario de entrada por día (cada fecha se compara con el día anterior).
                Se alerta si el costo varía ≥ 50% o ≥ S/ 1.00 por unidad. No incluye devoluciones.
              </p>
            </div>
            {dailySummary.length > 0 && (
              <button
                type="button"
                onClick={exportSummaryExcel}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                Exportar resumen Excel
              </button>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Almacén</label>
                <select
                  value={warehouseId}
                  onChange={(e) => setWarehouseId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos (detalle por almacén)</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={handleConsult}
                  disabled={loading}
                  className="w-full md:w-auto px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-md font-medium text-sm"
                >
                  {loading ? 'Consultando...' : 'Consultar'}
                </button>
              </div>
            </div>
            {meta && (
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <p>
                  Período: <strong>{formatShortDate(meta.date_from)}</strong> — <strong>{formatShortDate(meta.date_to)}</strong>
                </p>
                <p>
                  <strong>{meta.total_variations ?? 0}</strong> variación{(meta.total_variations ?? 0) === 1 ? '' : 'es'} en{' '}
                  <strong>{meta.period_days ?? 0}</strong> día{(meta.period_days ?? 0) === 1 ? '' : 's'}
                  {' '}({meta.days_with_variations ?? 0} con alertas)
                </p>
              </div>
            )}
            <div className="flex flex-wrap gap-2 text-sm mt-3">
              <Link to="/reports/inventory-dashboard" className="text-teal-600 hover:underline">Dashboard de inventario</Link>
              <span className="text-gray-300">·</span>
              <Link to="/reports/entries-exits" className="text-teal-600 hover:underline">Entradas y salidas</Link>
            </div>
          </div>

          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">Cargando...</div>
          ) : dailySummary.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-10 text-center">
              <p className="text-gray-600">No hay datos para el período seleccionado.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-800">Variaciones por día</h2>
                <p className="text-xs text-gray-500 mt-0.5">Haz clic en un día con variaciones para ver el detalle de productos.</p>
              </div>
              <div className="overflow-x-auto max-h-[420px] overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-teal-700 text-white text-left text-xs uppercase tracking-wide sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-semibold">Fecha</th>
                      <th className="px-4 py-3 font-semibold">Comparado con</th>
                      <th className="px-4 py-3 font-semibold text-right">Variaciones</th>
                      <th className="px-4 py-3 font-semibold text-right">Acción</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {dailySummary.map((row) => {
                      const isSelected = selectedDetailDate === row.reference_date;
                      const hasVariations = row.variation_count > 0;
                      return (
                        <tr
                          key={row.reference_date}
                          className={`${isSelected ? 'bg-teal-50' : ''} ${hasVariations ? 'hover:bg-amber-50/50' : 'text-gray-500'}`}
                        >
                          <td className="px-4 py-2.5 whitespace-nowrap font-medium text-gray-900">
                            {formatShortDate(row.reference_date)}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap text-gray-600">
                            {formatShortDate(row.previous_date)}
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            <span className={`inline-flex min-w-[2rem] justify-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                              hasVariations ? 'bg-amber-100 text-amber-900' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {row.variation_count}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {hasVariations ? (
                              <button
                                type="button"
                                onClick={() => loadDetailForDate(row.reference_date)}
                                className="text-teal-700 hover:text-teal-900 font-medium text-xs"
                              >
                                Ver detalle
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td className="px-4 py-2.5 font-semibold text-gray-800" colSpan={2}>Total del período</td>
                      <td className="px-4 py-2.5 text-right font-bold text-teal-800">
                        {meta?.total_variations ?? daysWithData.reduce((sum, row) => sum + row.variation_count, 0)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {selectedDetailDate && (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 bg-amber-50 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">
                    Detalle — {formatShortDate(selectedDetailDate)}
                  </h2>
                  {detailMeta && (
                    <p className="text-xs text-gray-600 mt-0.5">
                      vs {formatShortDate(detailMeta.previous_date)} · {detailMeta.total} variación{detailMeta.total === 1 ? '' : 'es'}
                    </p>
                  )}
                </div>
                {detailRows.length > 0 && (
                  <button
                    type="button"
                    onClick={exportDetailExcel}
                    className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-medium text-gray-800 hover:bg-gray-50"
                  >
                    Exportar detalle Excel
                  </button>
                )}
              </div>

              {detailLoading ? (
                <div className="p-10 text-center text-gray-500">Cargando detalle...</div>
              ) : detailRows.length === 0 ? (
                <div className="p-10 text-center text-gray-500">Sin variaciones para este día.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-teal-700 text-white text-left text-xs uppercase tracking-wide">
                      <tr>
                        {showWarehouse && <th className="px-4 py-3 font-semibold">Almacén</th>}
                        <th className="px-4 py-3 font-semibold">Producto</th>
                        <th className="px-4 py-3 font-semibold text-right">Día anterior</th>
                        <th className="px-4 py-3 font-semibold text-right">Costo unit. anterior</th>
                        <th className="px-4 py-3 font-semibold text-right">Día actual</th>
                        <th className="px-4 py-3 font-semibold text-right">Costo unit. actual</th>
                        <th className="px-4 py-3 font-semibold text-right">Variación costo</th>
                        <th className="px-4 py-3 font-semibold text-right">Cant. actual</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {detailRows.map((row) => (
                        <tr key={`${row.product_id}-${row.warehouse_id || 0}`} className="hover:bg-amber-50/40">
                          {showWarehouse && (
                            <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">
                              {row.warehouse_code || row.warehouse_name || '—'}
                            </td>
                          )}
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-gray-900">{row.name}</div>
                            {row.sku ? <div className="text-xs text-gray-500">{row.sku}</div> : null}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">
                            {formatShortDate(row.previous_date)}
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {formatMoney(row.previous_unit_cost)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">
                            {formatShortDate(row.current_date)}
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-amber-900 whitespace-nowrap">
                            {formatMoney(row.current_unit_cost)}
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            <span className={`font-semibold ${row.change_unit_cost >= 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                              {row.change_unit_cost >= 0 ? '+' : ''}{formatMoney(row.change_unit_cost)}
                            </span>
                            {row.change_ratio_pct != null && (
                              <span className="text-xs text-amber-700 ml-1">({formatPct(row.change_ratio_pct)})</span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right whitespace-nowrap">
                            {formatQty(row.current_qty)} {row.unit_of_measure || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default EntryChangeAlerts;
